import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_URL } from '../constants/urls';

type MatrixCell = {
  score: number | null;
  pipeline_value_unweighted: number;
  pipeline_value_weighted: number;
  opportunity_count: number;
};

type MatrixRow = {
  segment_id: string;
  segment_name: string;
  cells: { [propId: string]: MatrixCell };
};

type Proposition = {
  id: string;
  name: string;
};

export default function SegmentScoreMatrix() {
  const [matrix, setMatrix] = useState<MatrixRow[]>([]);
  const [propositions, setPropositions] = useState<Proposition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/positions/matrix`)
      .then((res) => {
        const data = res.data;
        // Get unique segments and propositions
        const segmentMap: { [id: string]: string } = {};
        const propMap: { [id: string]: string } = {};
        data.forEach((item: any) => {
          segmentMap[item.segment_id] = item.segment_name;
          propMap[item.proposition_id] = item.proposition_name;
        });
        const propList = Object.entries(propMap).map(([id, name]) => ({
          id,
          name,
        }));
        setPropositions(propList);
        // Build matrix rows
        const rows: MatrixRow[] = Object.entries(segmentMap).map(
          ([segId, segName]) => {
            const cells: { [propId: string]: MatrixCell } = {};
            propList.forEach((prop) => {
              const cell = data.find(
                (d: any) =>
                  d.segment_id === segId && d.proposition_id === prop.id,
              );
              cells[prop.id] = cell
                ? {
                    score: cell.score,
                    pipeline_value_unweighted: cell.pipeline_value_unweighted,
                    pipeline_value_weighted: cell.pipeline_value_weighted,
                    opportunity_count: cell.opportunity_count,
                  }
                : {
                    score: null,
                    pipeline_value_unweighted: 0,
                    pipeline_value_weighted: 0,
                    opportunity_count: 0,
                  };
            });
            return { segment_id: segId, segment_name: segName, cells };
          },
        );
        setMatrix(rows);
        setLoading(false);
      })
      .catch((_err) => {
        setError('Failed to load matrix');
        setLoading(false);
      });
  }, []);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Typography variant="h4" mb={2}>
        Segment Score Matrix
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Segment</TableCell>
              {propositions.map((p) => (
                <TableCell key={p.id} align="center">
                  {p.name}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {matrix.map((row) => (
              <TableRow key={row.segment_id}>
                <TableCell>{row.segment_name}</TableCell>
                {propositions.map((p) => {
                  const cell = row.cells[p.id];
                  return (
                    <TableCell key={p.id} align="center">
                      <div>Score: {cell.score ?? '-'}</div>
                      <div>
                        Pipeline:{' '}
                        {cell.pipeline_value_unweighted.toLocaleString(
                          undefined,
                          { maximumFractionDigits: 0 },
                        )}
                      </div>
                      <div>
                        Weighted:{' '}
                        {cell.pipeline_value_weighted.toLocaleString(
                          undefined,
                          { maximumFractionDigits: 0 },
                        )}
                      </div>
                      <div>Opps: {cell.opportunity_count}</div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
